// src/components/PasswordReset.js - Password reset flow
import React, { useState } from 'react';
import axios from 'axios';
import Button from './Button';
import Icon from './Icon';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function PasswordReset({ onBackToLogin }) {
  const [step, setStep] = useState('request'); // 'request' or 'success'
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE}/auth/password-reset/request`, {
        email: email
      });

      console.log('✅ Password reset request sent');
      setStep('success');
    } catch (err) {
      console.error('❌ Password reset request error:', err);
      setError(err.response?.data?.detail || 'Ett fel uppstod. Försök igen senare.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'success') {
    return (
      <div className="max-w-lg mx-auto my-8 p-8 border border-light rounded-lg bg-surface-primary shadow-custom-lg font-sans">
        <div className="text-center">
          <div className="mb-6">
            <Icon name="CheckCircle" className="w-16 h-16 text-success-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-primary mb-2 font-inter">E-post skickat!</h2>
          </div>
          
          <div className="bg-success-50 border border-success-500/20 rounded-lg p-6 mb-6">
            <p className="text-success-700 mb-4">
              Om e-postadressen finns i vårt system har ett återställningsmeddelande skickats.
            </p>
            <div className="text-sm text-success-600">
              <p className="mb-2"><strong>🔒 Säkerhetsdetaljer:</strong></p>
              <ul className="text-left space-y-1">
                <li>• Länken är giltig i endast 15 minuter</li>
                <li>• Länken kan endast användas en gång</li>
                <li>• Kontrollera din skräppost</li>
              </ul>
            </div>
          </div>

          <div className="space-y-3">
            <Button 
              onClick={onBackToLogin}
              variant="primary"
              className="w-full"
            >
              Tillbaka till inloggning
            </Button>
            
            <button 
              onClick={() => setStep('request')}
              className="w-full text-sm text-brand-primary hover:text-brand-primary-dark underline transition-custom"
            >
              Skicka nytt meddelande
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto my-8 p-8 border border-light rounded-lg bg-surface-primary shadow-custom-lg font-sans">
      <div className="text-center mb-6">
        <Icon name="LockKeyhole" className="w-12 h-12 text-brand-primary mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-primary mb-2 font-inter">Återställ lösenord</h2>
        <p className="text-secondary">
          Ange din e-postadress så skickar vi en säker återställningslänk.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1">
          <input
            type="email"
            placeholder="Din e-postadress"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-3 py-2 border border-light rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          />
        </div>
        
        {error && (
          <div className="bg-error-50 text-error-700 p-3 rounded border border-error-500/20 text-sm">
            {error}
          </div>
        )}

        <div className="bg-brand-primary/5 border border-brand-primary/20 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Shield" className="w-5 h-5 text-brand-primary mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-brand-primary mb-1">Säker återställning</h4>
              <ul className="text-xs text-brand-primary/80 space-y-1">
                <li>• Kryptografiskt säker länk</li>
                <li>• Giltig i endast 15 minuter</li>
                <li>• Kan endast användas en gång</li>
              </ul>
            </div>
          </div>
        </div>
        
        <Button type="submit" disabled={loading} variant="primary" className="w-full">
          {loading ? 'Skickar...' : 'Skicka återställningslänk'}
        </Button>
      </form>

      <div className="text-center mt-6 pt-4 border-t border-light">
        <button 
          onClick={onBackToLogin}
          className="text-brand-primary hover:text-brand-primary-dark underline text-sm transition-custom"
        >
          ← Tillbaka till inloggning
        </button>
      </div>
    </div>
  );
}

export default PasswordReset;
